import { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import CopyButton from '@/components/ui/CopyButton/CopyButton';
import downloadIcon from '@/assets/icons/download.svg';
import eyeIcon from '@/assets/icons/eye.svg';
import externalLinkIcon from '@/assets/icons/external-link.svg';
import styles from '../FundSettings.module.css';

/**
 * One row of «אישורים ומסמכים» (HAT-1231): what it is on one side, what you can do with it on the
 * other. Every action is a labelled control with its glyph - «צפייה» with the eye, «הורדה» with the
 * arrow - the way files are viewed and downloaded everywhere else in the portal, so the name stays
 * text (Chaya, 22.9: a name that is secretly a link is not how the rest of the app behaves).
 */
interface DocumentRowProps {
  name: string;
  /** A second, quieter line under the name: what the document is for, when it was updated. */
  note?: ReactNode;
  /** Where «צפייה» leads when the document is published at an address. */
  href?: string;
  /** «צפייה» for a document that has to be fetched with the caller's token (a server-generated form). */
  onView?: () => void;
  /** The address is a page on another site, not a document: «פתיחה» with the external-link glyph. */
  external?: boolean;
  /** Saving the file; rendered as «הורדה». */
  onDownload?: () => void;
  isBusy?: boolean;
  /** An address a coordinator forwards - to a donor, to an accountant; rendered as the shared copy control. */
  copyValue?: string;
  /** The row's own extra controls (details popups, share chips), placed before the standard ones. */
  children?: ReactNode;
}

const DocumentRow = ({ name, note, href, onView, external, onDownload, isBusy, copyValue, children }: DocumentRowProps) => {
  const { t } = useTranslation();
  const viewLabel = external ? t('fundSettingsDocumentsPage.openPage') : t('fundSettingsDocumentsPage.viewDocument');
  const viewIcon = external ? externalLinkIcon : eyeIcon;

  return (
    <div className={styles.documentRow}>
      <span className={styles.documentName}>
        <span>{name}</span>
        {note && <span className={styles.documentNote}>{note}</span>}
      </span>
      <span className={styles.rowActions}>
        {children}
        {href && (
          <a href={href} target="_blank" rel="noreferrer" className={styles.linkBtn}>
            <img src={viewIcon} alt="" className={styles.actionIcon} />
            {viewLabel}
          </a>
        )}
        {!href && onView && (
          <button type="button" className={styles.linkBtn} onClick={onView} disabled={isBusy}>
            <img src={viewIcon} alt="" className={styles.actionIcon} />
            {isBusy ? t('loading') : viewLabel}
          </button>
        )}
        {onDownload && (
          <button type="button" className={styles.linkBtn} onClick={onDownload} disabled={isBusy}>
            <img src={downloadIcon} alt="" className={styles.actionIcon} />
            {isBusy ? t('loading') : t('fundSettingsDocumentsPage.downloadDocument')}
          </button>
        )}
        {copyValue && <CopyButton value={copyValue} />}
      </span>
    </div>
  );
};

export default DocumentRow;
